/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import {
    Component,
    Input,
    OnInit,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { MessageItem } from 'message-item';
import { RepeatedMessageItem } from 'repeated-message-item';
import { SchemaService } from 'ajs/modules/core/services/schema-service';

import {
    IComponentAddOption,
    ISelectedComponent,
} from 'ng/modules/avi-component-rendering/components/avi-component-adder';

import * as globalL10n from 'global-l10n';
import * as l10n from './match-adder.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type IComponentBindings = Record<string, unknown>;

/**
 * Interface of the match option object. Used to render the match component. This
 */
export interface IMatchOption extends IComponentAddOption, ISelectedComponent {
    label: string;
    subLabel?: string;
    objectType?: string;
    fieldName?: string;
    component: Type<Component>;
    componentBindings?: IComponentBindings;
}

/**
 * Additional properties added to the component bindings. These are passed to every match component.
 */
interface IExtendedComponentBindings extends IComponentBindings {
    label: string;
    subLabel: string;
    objectType: string;
    matchObjectType: string;
    editable: MessageItem;
    onRemoveMatch(): void;
}

/**
 * A selected option has the same fields as a match option with additional fields added to componentBindings.
 */
interface ISelectedOption extends IMatchOption {
    componentBindings: IExtendedComponentBindings;
}

/**
 * Properties of match input binding, typically a match config item.
 */
interface IMatchType {
    addMatch?: (entryFieldName: string) => void;
    hasMatchByField?: (matchFieldName: string) => boolean;
    config?: Record<string, any>;
}

/**
 * @description
 *     This component allows for developers to pass in a list of IMatchOptions for rendering match components and should
 *     be used with messageItems only.
 *     Users are able to add matches from the dropdown button and remove them. Matches that have been added will be
 *     removed from the dropdown button options, unless they're repeated.
 *     Repeated matches can be added multiple times and its component will be rendered multiple times.
 *
 *     TODO: We can create wrappers around MatchAdderComponent for specific match messages like
 *     MatchTarget that has a predefined list of IMatchOptions.
 * @author alextsg
 */
@Component({
    selector: 'match-adder',
    templateUrl: './match-adder.component.html',
})
export class MatchAdderComponent implements OnInit {
    /**
     * Match message item, extended from PolicyMatchConfigItem.
     */
    @Input()
    public match: IMatchType;

    /**
     * List of match options that the user can add. These options will be used to create the dropdown button options.
     */
    @Input()
    public matchOptions: IMatchOption[] = [];

    /**
     * Flag to disable the add button
     */
    @Input()
    public disableAddButton = false;

    /**
     * List of matches that can be added.
     */
    public selectableOptions: IMatchOption[] = [];

    /**
     * List of matches already selected.
     */
    public selectedOptions: ISelectedOption[] = [];

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        private readonly schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.setSelectedOptions();
        this.setSelectableOptions();
    }

    /**
     * Add a match config and match component when the user has selected a match from the Add dropdown button.
     */
    public addMatch(dropdownAction: IMatchOption): void {
        const { fieldName } = dropdownAction;
        let messageItem: MessageItem;

        if (this.isRepeatedMatch(fieldName)) {
            const repeatedMessageItem = this.match.config[fieldName];

            repeatedMessageItem.add();

            messageItem = repeatedMessageItem.at(repeatedMessageItem.count - 1);
        } else {
            this.match.addMatch(fieldName);

            messageItem = this.match.config[fieldName];
        }

        this.addSelectedOption(dropdownAction, messageItem);
        this.setSelectableOptions();
    }

    /**
     * Delete the messageItem from the match messageItem and remove the associated match component.
     */
    private removeMatch(fieldName: string, editable: MessageItem, selectedOptionIndex = 0): void {
        if (this.isRepeatedMatch(fieldName)) {
            this.match.config[fieldName].removeByMessageItem(editable);
        } else {
            delete this.match.config[fieldName];
        }

        this.selectedOptions = [
            ...this.selectedOptions.slice(0, selectedOptionIndex),
            ...this.selectedOptions.slice(selectedOptionIndex + 1),
        ];

        this.setSelectableOptions();
    }

    /**
     * Set the AviDropdownButton options. A match can only be selected if it wasn't already selected, or if it's a
     * repeated match.
     */
    private setSelectableOptions(): void {
        this.selectableOptions = this.matchOptions.filter(option => {
            const { fieldName, objectType } = option;

            if (this.isRepeatedMatch(fieldName)) {
                let maxElements = Infinity;

                try {
                    maxElements = this.schemaService.getFieldMaxElements(objectType, fieldName);
                } catch (e) {
                    // empty catch block
                }

                return this.match.config[fieldName].count < maxElements;
            }

            return !this.match.hasMatchByField(fieldName);
        });
    }

    /**
     * Create a selected option object by extending componentBindings for rendering a match component.
     */
    private createSelectedOption(dropdownAction: IMatchOption, editable: MessageItem): ISelectedOption {
        const {
            fieldName,
            label,
            subLabel,
            objectType,
            componentBindings = {},
        } = dropdownAction;

        const option = {
            ...dropdownAction,
        };

        option.componentBindings = {
            label,
            subLabel,
            objectType,
            fieldName,
            matchObjectType: this.getMatchComponentObjectType(objectType, fieldName),
            onRemoveMatch: () => {
                const selectedOptionIndex = this.selectedOptions.indexOf(option as ISelectedOption);

                this.removeMatch(fieldName, editable, selectedOptionIndex);
            },
            editable,
            ...componentBindings,
        };

        return option as ISelectedOption;
    }

    /**
     * Add an option to the list of selectedOptions to be rendered.
     */
    private addSelectedOption(dropdownAction: IMatchOption, messageItem: MessageItem): void {
        const selectedComponent = this.createSelectedOption(dropdownAction, messageItem);

        this.selectedOptions = this.selectedOptions.concat(selectedComponent);
    }

    /**
     * Add all selected options from existing configured matches.
     */
    private setSelectedOptions(): void {
        const selectedOptions: ISelectedOption[] = [];

        this.matchOptions.forEach((matchOption: IMatchOption): void => {
            const { fieldName } = matchOption;

            if (this.match.hasMatchByField(fieldName)) {
                if (this.isRepeatedMatch(fieldName)) {
                    this.match.config[fieldName].config.forEach((messageItem: MessageItem) => {
                        selectedOptions.push(this.createSelectedOption(matchOption, messageItem));
                    });
                } else {
                    selectedOptions.push(this.createSelectedOption(matchOption, this.match.config[fieldName]));
                }
            }
        });

        this.selectedOptions = selectedOptions;
    }

    /**
     * Return true if a field is a repeated match.
     */
    private isRepeatedMatch(fieldName: string): boolean {
        return this.match.config[fieldName] instanceof RepeatedMessageItem;
    }

    /**
     * Return the objectType for a child match config.
     */
    private getMatchComponentObjectType(parentObjectType: string, fieldName: string): string {
        const fields = this.schemaService.getMessageFields(parentObjectType);

        return fields?.[fieldName]?.objectType || '';
    }
}
